/**
 * SDK helpers for the patient-public QR flow (Phase 4e).
 *
 * These talk to the public Edge Functions:
 *   - verify-qr-token   (anon, GET ?token | ?short_code)
 *   - submit-reorder    (anon, POST { token | short_code, ... })
 *
 * Plus admin-side queries against the Phase 4e SQL views:
 *   - admin_order_qr_summary
 *   - admin_order_reorders
 *
 * Plus the authenticated generate-qr-token Edge Function for admin/podiatrist UI.
 *
 * The Edge Functions return the standard response envelope: { data, error }.
 * We surface `error` as a thrown Error for use with TanStack Query mutations.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { readEdgeErrorBody, unwrapEdge } from '../util/edge';

/**
 * Turn a supabase-js `functions.invoke` transport error into an Error carrying
 * the function's ACTUAL reason. supabase-js throws FunctionsHttpError with a
 * generic message ("Edge Function returned a non-2xx status code") and stashes
 * the Response on `error.context`; we read the `{ error: { code, message } }`
 * envelope so the UI banner self-explains (e.g. "Order line has no patient")
 * instead of the opaque non-2xx text.
 */
async function toInvokeError(error: unknown): Promise<Error> {
  const body = await readEdgeErrorBody(error);
  const msg = body?.error?.message ?? body?.message;
  if (msg) {
    const e = new Error(msg) as Error & { code?: string };
    if (body?.error?.code) e.code = body.error.code;
    return e;
  }
  return error instanceof Error ? error : new Error(String(error));
}

// -------------------- public types --------------------

export interface PatientPublicView {
  patient_first_name: string;
  /** The clinic that MADE the insoles (orders.clinic_id, since 20270358). */
  clinic_name: string;
  clinic_phone: string | null;
  /**
   * 20270358: the clinic that made the insoles has reorders switched on.
   * false means the reorder section does not exist on the page AND
   * submit-reorder refuses a request for this order (trigger on insert).
   */
  reorders_enabled: boolean;
  /** 20270358: the podiatrist who made them. Display name only, never contact details. */
  podiatrist_name: string | null;
  /** 20270358: top cover material from the line (e.g. "EVA 2mm", "Poron"; sandal cover for sandals). */
  top_cover_material: string | null;
  /** 20270358: top cover layer (e.g. "Leather", "DryFeet"; "None" is not shown). Always null for a sandal since 20270359. */
  top_cover_layer: string | null;
  /**
   * 20270359: the line is an orthopaedic sandal (line_type OR solution_subtype,
   * so a legacy sandal stored as custom_insole counts). Display only:
   * product_kind still derives from line_type and selects the reorder price.
   * Optional because verify-qr-token forwards these from v34.
   */
  is_sandal?: boolean;
  /** 20270359: 'buckle' | 'velcro' | 'flipflop', whitelisted server-side. */
  sandal_model?: string | null;
  /** 20270359: 'black' | 'grey' | 'beige' | 'blue', whitelisted server-side. */
  sandal_base_color?: string | null;
  /** 20270359: the upper strap colour, whitelisted server-side. */
  sandal_upper_strap_color?: string | null;
  product_name: string;
  /** Reorder-v2: material from the line configuration (e.g. "EVA"). */
  material: string | null;
  /** Reorder-v2: which preset price applies; null = not reorderable by preset. */
  product_kind: 'insole' | 'sandal' | null;
  /** Reorder-v2: preset GROSS unit price in AED, VAT included (qty-1 base). */
  reorder_price_aed: number | null;
  /** Reorder-v2.1: % off per unit at qty 2 (volume discount tier). */
  reorder_tier2_pct: number | null;
  /** Reorder-v2.1: % off per unit at qty 3+ (volume discount tier). */
  reorder_tier3_pct: number | null;
  /** Reorder-v2.2: marginal price of the 2nd pair (gross, VAT included). */
  reorder_second_pair_gross_aed: number | null;
  /** Reorder-v2.2: marginal price of each pair from the 3rd on (gross, VAT included). */
  reorder_additional_pair_gross_aed: number | null;
  /**
   * Reorder cart R1: the cross-sellable OPPOSITE product for this anchor
   * (insole anchor -> 'sandal'; sandal anchor -> 'insole'). null hides the
   * add-on box. These prices are SERVER-computed; the client only renders
   * them (never derives a cross price).
   */
  cross_kind?: 'sandal' | 'insole' | null;
  /** Cross add-on full per-pair gross (VAT included), e.g. 2000 / 1500. */
  cross_unit_full_aed?: number | null;
  /** Cross add-on discounted per-pair gross (VAT included), e.g. 1800 / 1200. */
  cross_unit_discounted_aed?: number | null;
  /**
   * Anchor qty at/above which the discounted cross price applies. Sandals
   * cross-sold on an insole anchor: 2 (full price until the anchor is 2+
   * pairs, then the single hard-capped discount). Insoles cross-sold on a
   * sandal anchor: 1 (discounted from the first pair).
   */
  cross_discount_applies_from_anchor_qty?: number | null;
  /**
   * Reorder cart R2: FITO stock-shoe offer (both anchors). Server-priced;
   * colours (black/grey/blue) and the size list come from the static catalog
   * in the UI. fito_prefill_size is derived from the original order's foot
   * size (a range prefills its upper bound) and the patient can adjust it.
   */
  fito_offered?: boolean | null;
  fito_gross_aed?: number | null;
  fito_retail_reference_aed?: number | null;
  fito_prefill_size?: string | null;
  /** Order header status (matches the visible 5-step timeline) */
  order_status:
    | 'draft'
    | 'submitted'
    | 'in_review'
    | 'in_production'
    | 'in_delivery'
    | 'delivered'
    | 'cancelled'
    | 'returned'
    | 'on_hold';
  /** Line status (more granular but we only display order_status). */
  line_status: string;
  expected_delivery_date: string | null;
  delivered_at: string | null;
  actions: { can_request_reorder: boolean };
}

export interface SubmitReorderInput {
  token?: string;
  short_code?: string;
  /** Pairs to produce (integer 1..10). Defaults to 1 server-side. */
  quantity?: number;
  message?: string;
  preferred_contact?: 'email' | 'phone' | 'whatsapp';
  preferred_language?: 'en' | 'ar' | 'es';
  patient_phone?: string;
  patient_email?: string;
  update_contact?: boolean;
  /**
   * Reorder cart cross-sell add-ons. OMITTED entirely (not []) for a plain
   * reorder, so the no-add request body stays byte-identical to reorder-v2.2.
   * Server prices each item via reorder_cart_quote; the client never sends a
   * price. cross_sandal / cross_insole carry only a quantity; fito_shoe (R2)
   * also carries the chosen colour + size (the server resolves + validates the
   * F1 stock sku).
   */
  items?: Array<
    | { item_kind: 'cross_sandal' | 'cross_insole'; quantity: number }
    | { item_kind: 'fito_shoe'; quantity: number; fito_color: string; fito_size: string }
  >;
}

export interface SubmitReorderResult {
  reorder_id: string;
  status: 'requested' | 'awaiting_payment';
  /** Present when Stripe is configured. Patient should be redirected here. */
  checkout_url?: string;
  session_id?: string;
  /** True when Stripe keys are PHASE_4_TODO — UI should show fallback banner. */
  stub?: boolean;
  message?: string;
}

export interface GenerateQrTokenInput {
  order_line_id: string;
  regenerate?: boolean;
  expires_in_days?: number;
}

export interface GenerateQrTokenResult {
  qr_id: string;
  short_code: string;
  expires_at: string | null;
  /** Raw bearer token + full URL: present ONLY on a fresh generation; null when
   *  an active token already existed (it is never stored, so not re-derivable). */
  token: string | null;
  full_url: string | null;
  short_url: string;
  png_data_url: string | null;
  /** True when a redundant call hit an existing active token — a no-op success,
   *  not a fresh mint. The caller should refresh, not re-open the one-time panel. */
  already_active?: boolean;
}

// -------------------- helpers --------------------

interface EnvelopeOk<T> { data: T; error: null }
interface EnvelopeErr { data: null; error: { code: string; message: string; details?: unknown } }
type Envelope<T> = EnvelopeOk<T> | EnvelopeErr;

/** Read a Vite-style env var. Falls back to process.env when running in Node
 *  (e.g. Vitest with default node environment), so tests can inject values
 *  via `process.env.VITE_*` without needing a jsdom/Vite test env.
 *
 *  Each value is read as a LITERAL `import.meta.env.VITE_*` member expression
 *  so Vite statically inlines it in the production build. Reading through an
 *  aliased object (`const m = import.meta; m.env[name]`) or a computed key
 *  (`import.meta.env[name]`) is NOT inlined by Vite and resolves to undefined
 *  in production (which previously left functionsUrl() empty and broke every
 *  patient-public page). The `as any` casts satisfy the SDK's non-Vite tsc and
 *  are erased before Vite's replacement runs; the try/catch guards the rare
 *  runtime (neither Vite nor Vitest) where `import.meta.env` is absent.
 *
 *  `name` is a closed union of the keys this module needs: adding a fourth key
 *  requires a matching literal ternary branch below, so a new/typo'd name is a
 *  compile error rather than a silent prod-only miss. */
type ViteEnvKey = 'VITE_FUNCTIONS_URL' | 'VITE_SUPABASE_URL' | 'VITE_SUPABASE_ANON_KEY';
function readEnv(name: ViteEnvKey): string | undefined {
  let fromMeta: unknown;
  try {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    fromMeta =
      name === 'VITE_FUNCTIONS_URL'
        ? (import.meta as any).env.VITE_FUNCTIONS_URL
        : name === 'VITE_SUPABASE_URL'
          ? (import.meta as any).env.VITE_SUPABASE_URL
          : name === 'VITE_SUPABASE_ANON_KEY'
            ? (import.meta as any).env.VITE_SUPABASE_ANON_KEY
            : undefined;
    /* eslint-enable @typescript-eslint/no-explicit-any */
  } catch {
    fromMeta = undefined;
  }
  if (typeof fromMeta === 'string' && fromMeta.length > 0) return fromMeta;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const proc: any = (globalThis as any).process;
  const fromProc = proc?.env?.[name];
  if (typeof fromProc === 'string' && fromProc.length > 0) return fromProc;
  return undefined;
}

function functionsUrl(): string {
  const explicit = readEnv('VITE_FUNCTIONS_URL');
  if (explicit) return explicit.replace(/\/$/, '');
  const supa = readEnv('VITE_SUPABASE_URL');
  if (supa) return `${supa.replace(/\/$/, '')}/functions/v1`;
  return '';
}

async function callPublicFn<T>(
  path: string,
  init: RequestInit & { method: 'GET' | 'POST' },
): Promise<T> {
  const base = functionsUrl();
  if (!base) throw new Error('Edge function URL not configured');
  const anonKey = readEnv('VITE_SUPABASE_ANON_KEY');
  const headers = new Headers(init.headers);
  // Public Edge Functions still require apikey header on Supabase
  if (anonKey) headers.set('apikey', anonKey);
  if (anonKey && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${anonKey}`);
  }
  if (init.method === 'POST' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${base}${path}`, { ...init, headers });
  const env2 = (await res.json()) as Envelope<T>;
  if (env2.error) {
    const err = new Error(env2.error.message) as Error & { code?: string };
    err.code = env2.error.code;
    throw err;
  }
  return env2.data;
}

// -------------------- public Edge Function calls --------------------

export async function verifyQrToken(args: {
  token?: string;
  shortCode?: string;
}): Promise<PatientPublicView> {
  const params = new URLSearchParams();
  if (args.token) params.set('token', args.token);
  if (args.shortCode) params.set('short_code', args.shortCode);
  return callPublicFn<PatientPublicView>(`/verify-qr-token?${params.toString()}`, {
    method: 'GET',
  });
}

export async function submitReorder(input: SubmitReorderInput): Promise<SubmitReorderResult> {
  return callPublicFn<SubmitReorderResult>('/submit-reorder', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function generateQrToken(
  sb: SupabaseClient,
  input: GenerateQrTokenInput,
): Promise<GenerateQrTokenResult> {
  // Authenticated call — the supabase-js wrapper attaches the user JWT.
  const { data, error } = await sb.functions.invoke<unknown>(
    'generate-qr-token',
    { body: input },
  );
  if (error) throw await toInvokeError(error);
  return unwrapEdge<GenerateQrTokenResult>(data);
}

export interface MintOrderTokensResult {
  order_id: string;
  minted: number;
  results: Array<{
    line_id: string;
    status: 'minted' | 'existing' | 'skipped_no_patient' | 'skipped_revoked' | 'error';
    short_code?: string;
    reason?: string;
  }>;
}

/**
 * Batch-mint QR tokens for all eligible (custom insole/sandal) lines of an
 * order that lack an active token. Idempotent. Used by the wizard after
 * submit, and by the admin order QR section (lazy self-heal + explicit
 * button). Authenticated as admin/podiatrist (JWT attached by supabase-js).
 */
export async function mintOrderTokens(
  sb: SupabaseClient,
  orderId: string,
): Promise<MintOrderTokensResult> {
  const { data, error } = await sb.functions.invoke<unknown>('mint-order-tokens', {
    body: { order_id: orderId },
  });
  if (error) throw await toInvokeError(error);
  return unwrapEdge<MintOrderTokensResult>(data);
}

// -------------------- admin queries --------------------

export interface AdminQrSummaryRow {
  order_id: string;
  line_id: string;
  qr_id: string | null;
  short_code: string | null;
  qr_state: 'active' | 'expired' | 'revoked' | null;
  qr_created_at: string | null;
  qr_expires_at: string | null;
  scan_count: number | null;
  last_scanned_at: string | null;
  reorder_count: number | null;
}

export interface AdminReorderRow {
  id: string;
  qr_token_id: string;
  original_order_line_id: string;
  source_order_id: string;
  status: 'requested' | 'reviewed' | 'fulfilled' | 'rejected';
  patient_message: string | null;
  preferred_contact: string | null;
  preferred_language: string | null;
  created_at: string;
  reviewed_at: string | null;
  fulfilled_order_line_id: string | null;
  new_order_id: string | null;
  new_order_number: string | null;
  short_code: string;
}

export const patientPublicKeys = {
  qrSummary: (orderId: string) => ['admin', 'order-qr-summary', orderId] as const,
  reorders: (orderId: string) => ['admin', 'order-reorders', orderId] as const,
};

export function orderQrSummaryQuery(sb: SupabaseClient, orderId: string) {
  return {
    queryKey: patientPublicKeys.qrSummary(orderId),
    queryFn: async () => {
      const { data, error } = await sb
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('admin_order_qr_summary' as any)
        .select(
          'order_id, line_id, qr_id, short_code, qr_state, qr_created_at, qr_expires_at, scan_count, last_scanned_at, reorder_count',
        )
        .eq('order_id', orderId);
      if (error) throw error;
      return (data ?? []) as unknown as AdminQrSummaryRow[];
    },
  };
}

export function orderReordersQuery(sb: SupabaseClient, orderId: string) {
  return {
    queryKey: patientPublicKeys.reorders(orderId),
    queryFn: async () => {
      const { data, error } = await sb
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('admin_order_reorders' as any)
        .select(
          'id, qr_token_id, original_order_line_id, source_order_id, status, patient_message, preferred_contact, preferred_language, created_at, reviewed_at, fulfilled_order_line_id, new_order_id, new_order_number, short_code',
        )
        .eq('source_order_id', orderId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as AdminReorderRow[];
    },
  };
}
